/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts
import Dashboard from "layouts/dashboard";
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import ResetPassword from "layouts/authentication/reset-password/cover";
import ConfirmPassword from "layouts/authentication/confirm-password";
import ConfirmEmail from "layouts/authentication/confirm-email";
import { CreateEditRole, ListRole } from "layouts/roles";
import { ListUser } from "layouts/users";
import { ListParameters, EditParameters } from "layouts/parameters";
import { ListMachine, CreateEditMachine } from "layouts/machines";
import { ListDevice, CreateEditDevice } from "layouts/devices";
import { ListProductLine, CreateEditProductLine } from "layouts/product_lines";
import { ListProductionLine, CreateEditProductionLine } from "layouts/production_lines";
import { Import, ProductArrays } from "layouts/product_arrays";
import { MesReport, IrlReport, CtrCantidadesReport, AndonWeeklyReport, MatrizABCReport } from "layouts/reports";
import permissions from "permissions";
import { ListArnes } from "layouts/proarnes";
import { Counterpart } from "layouts/product_counterpart";
import { Itemrotation } from "layouts/product_rotation";
import { Itemprovider } from "layouts/product_provider";
import { ArnesProduced } from "layouts/produced_list_arnes";
import { ImageComponents } from "layouts/images_component_arnes";
import { OrderList } from "layouts/order_list";
import FollowUpBoard from "layouts/follow-up/FollowUpBoard";

// @mui icons
import Icon from "@mui/material/Icon";


const routes = [
  {
    type: "collapse",
    name: "Root",
    key: "root",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/",
    component: <SignIn />,
    protected_route: false,
    navbar: false
  },
  {
    type: "collapse",
    name: "Iniciar Sesión",
    key: "sign-in",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/authentication/sign-in",
    component: <SignIn />,
    protected_route: false,
    navbar: false
  },
  {
    type: "collapse",
    name: "Registrar",
    key: "register",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/authentication/sign-up",
    component: <SignUp />,
    protected_route: false,
    navbar: false
  },
  {
    type: "collapse",
    name: "Restaurar Contraseña",
    key: "reset-password",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/authentication/reset-password",
    component: <ResetPassword />,
    protected_route: false,
    navbar: false
  },
  {
    type: "collapse",
    name: "Confirmar Contraseña",
    key: "confirm-password",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/authentication/confirm-password",
    component: <ConfirmPassword />,
    protected_route: false,
    navbar: false
  },
  {
    type: "collapse",
    name: "Verificar Email",
    key: "verify",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/api/email/verify/:id",
    component: <ConfirmEmail />,
    protected_route: false,
    navbar: false
  },

  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
    protected_route: true,
    navbar: false,
  },
  {
    type: "collapse",
    name: "Seguimiento OC",
    key: "follow-up",
    icon: <Icon fontSize="small">assignment_turned_in</Icon>,
    route: "/follow-up",
    component: <FollowUpBoard />,
    protected_route: true,
    navbar: true,
    permissions: permissions.roles,
  },
  {
    type: "collapse",
    name: "Usuarios",
    key: "users",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/users",
    component: <ListUser />,
    protected_route: true,
    navbar: true,
    permissions: ["user_list", "user_create", "user_edit", "user_activate", "role_assign"],
  },
  {
    type: "collapse",
    name: "Roles",
    key: "roles",
    icon: <Icon fontSize="small">people</Icon>,
    route: "/roles",
    component: <ListRole />,
    protected_route: true,
    navbar: true,
    permissions: permissions.roles,
  },
  {
    type: "collapse",
    name: "Perfil",
    key: "profile",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/profile",
    component: <Profile />,
    protected_route: true,
    navbar: false,
  },
  {
    type: "collapse",
    name: "Crear Rol",
    key: "role-create",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/roles/create",
    component: <CreateEditRole />,
    protected_route: true,
    navbar: false,
    permissions: permissions.roles[1],
  },
  {
    type: "collapse",
    name: "Editar Rol",
    key: "role-edit",
    icon: <Icon fontSize="small">person</Icon>,
    route: "roles/:id/edit",
    component: <CreateEditRole />,
    protected_route: true,
    navbar: false,
    permissions: permissions.roles[2],
  },

  {
    type: "collapse",
    name: "Máquinas",
    key: "machines",
    icon: <Icon fontSize="small">precision_manufacturing</Icon>,
    route: "/machines",
    component: <ListMachine />,
    protected_route: true,
    navbar: true,
    permissions: permissions.machines,
  },
  {
    type: "collapse",
    name: "Crear Máquina",
    key: "machine-create",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/machines/create",
    component: <CreateEditMachine />,
    protected_route: true,
    navbar: false,
    permissions: permissions.machines[1],
  },
  {
    type: "collapse",
    name: "Editar Máquina",
    key: "machine-edit",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/machines/:id/edit",
    component: <CreateEditMachine />,
    protected_route: true,
    navbar: false,
    permissions: permissions.machines[2],
  },
  {
    type: "collapse",
    name: "Dispositivos",
    key: "devices",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/devices",
    component: <ListDevice />,
    protected_route: true,
    navbar: false,
    permissions: permissions.devices,
  },
  {
    type: "collapse",
    name: "Crear Dispositivo",
    key: "device-create",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/devices/create",
    component: <CreateEditDevice />,
    protected_route: true,
    navbar: false,
    permissions: permissions.devices[1],
  },
  {
    type: "collapse",
    name: "Líneas de Producción",
    key: "production-lines",
    icon: <Icon fontSize="small">category</Icon>,
    route: "/production-lines",
    component: <ListProductionLine />,
    protected_route: true,
    navbar: true,
    permissions: permissions.production_lines,
  },
  {
    type: "collapse",
    name: "Crear Línea de Producción",
    key: "production-line-create",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/production-lines/create",
    component: <CreateEditProductionLine />,
    protected_route: true,
    navbar: false,
    permissions: permissions.production_lines[1],
  },
  {
    type: "collapse",
    name: "Editar Línea de Producción",
    key: "production-line-edit",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/production-lines/:id/edit",
    component: <CreateEditProductionLine />,
    protected_route: true,
    navbar: false,
    permissions: permissions.production_lines[2],
  },
  {
    type: "collapse",
    name: "Líneas de Producto",
    key: "product-lines",
    icon: <Icon fontSize="small">widgets</Icon>,
    route: "/product-lines",
    component: <ListProductLine />,
    protected_route: true,
    navbar: true,
    permissions: permissions.product_lines,
  },
  {
    type: "collapse",
    name: "Crear Línea de Producto",
    key: "product-lines-create",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-lines/create",
    component: <CreateEditProductLine />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_lines[1],
  },
  {
    type: "collapse",
    name: "Editar Línea de Producto",
    key: "product-lines-edit",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-lines/:id/edit",
    component: <CreateEditProductLine />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_lines[2],
  },
  {
    type: "collapse",
    name: "Matrices de Productos",
    key: "product-arrays",
    icon: <Icon fontSize="small">table_chart</Icon>,
    route: "/product-arrays",
    component: <ProductArrays />,
    protected_route: true,
    navbar: true,
    permissions: permissions.product_arrays,
  },
  {
    type: "collapse",
    name: "Reportes",
    key: "kanban-report",
    icon: <Icon fontSize="small">bar_chart</Icon>,
    route: "/kanban-reportd", // Ruta base para el collapse, puedes ajustarla
    protected_route: true,
    navbar: true,
    collapse: [
      {
        name: "Matriz ABC",
        key: "matriz-abc",
        icon: <Icon fontSize="small">grid_on</Icon>,
        route: "/MatrizABCReport",
        component: <MatrizABCReport />,
        protected_route: true,
        navbar: true,
        permissions: permissions.roles,
      },
      {
        name: "CTR Cantidades",
        key: "CTR_cantidades",
        icon: <Icon fontSize="small">format_list_numbered</Icon>,
        route: "/CtrCantidadesReport",
        component: <CtrCantidadesReport />,
        protected_route: true,
        navbar: true,
        permissions: permissions.roles,
      },
      {
        name: "CTR Valores",
        key: "CTR_Valores",
        icon: <Icon fontSize="small">paid</Icon>,
        route: "/kanban-report",
        component: <MesReport />,
        protected_route: true,
        navbar: true,
        permissions: permissions.roles,
      },
      {
        name: "IRL",
        key: "irl-report",
        icon: <Icon fontSize="small">insights</Icon>,
        route: "/irl-report",
        component: <IrlReport />,
        protected_route: true,
        navbar: true,
        permissions: permissions.product_arrays[2],
      }
    ]
  },
  {
    type: "collapse",
    name: "ANDON",
    key: "andon-report",
    icon: <Icon fontSize="small">traffic</Icon>,
    route: "/andon-reportd", // Ruta base para el collapse, puedes ajustarla
    protected_route: true,
    navbar: true,
    collapse: [
      {
        name: "Reporte Semanal",
        key: "andon-weekly-report",
        icon: <Icon fontSize="small">table_chart</Icon>,
        route: "/AndonWeeklyReport",
        component: <AndonWeeklyReport />,
        protected_route: true,
        navbar: true,
        permissions: permissions.roles,
      }
    ]
  },
  {
    type: "collapse",
    name: "Importar Historial de Consumo",
    key: "consumption-history-import",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-arrays/consumption-history-import",
    component: <Import />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_arrays[6],
  },
  {
    type: "collapse",
    name: "Importar Maestro de Saldos",
    key: "balance-master-import",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-arrays/balance-master-import",
    component: <Import />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_arrays[7],
  },
  {
    type: "collapse",
    name: "Importar Maestro EP",
    key: "ep-master-import",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-arrays/ep-master-import",
    component: <Import />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_arrays[8],
  },
  {
    type: "collapse",
    name: "Importar Maestro Producto Terminado",
    key: "finished-product-master-import",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-arrays/finished-product-master-import",
    component: <Import />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_arrays[9],
  },
  {
    type: "collapse",
    name: "Importar Maestro de Materiales",
    key: "material-master-import",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-arrays/material-master-import",
    component: <Import />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_arrays[10],
  },
  {
    type: "collapse",
    name: "Importar Maestro OC",
    key: "oc-master-import",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-arrays/oc-master-import",
    component: <Import />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_arrays[11],
  },
  {
    type: "collapse",
    name: "Importar Maestro de Pedidos",
    key: "order-master-import",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-arrays/order-master-import",
    component: <Import />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_arrays[12],
  },
  {
    type: "collapse",
    name: "Importar Valor Unitario",
    key: "unit-value-import",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-arrays/unit-value-import",
    component: <Import />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_arrays[13],
  },
  {
    type: "collapse",
    name: "Importar Historial de Consumo Producto Terminado",
    key: "consumption-history-finished-product-import",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-arrays/consumption-history-finished-product-import",
    component: <Import />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_arrays[14],
  },
  {
    type: "collapse",
    name: "Importar Maestro OP",
    key: "op-master-import",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-arrays/op-master-import",
    component: <Import />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_arrays[15],
  },
  {
    type: "collapse",
    name: "Importar Valores Nominales",
    key: "vn-pt-uh-import",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-arrays/vn-pt-uh-import",
    component: <Import />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_arrays[16],
  },
  {
    type: "collapse",
    name: "Importar Maestro de Materiales Exclusivos",
    key: "material-master-exclusive-import",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/product-arrays/material-master-exclusive-import",
    component: <Import />,
    protected_route: true,
    navbar: false,
    permissions: permissions.product_arrays[17],
  },
  {
    type: "collapse",
    name: "Parámetros",
    key: "parameters",
    icon: <Icon fontSize="small">tune</Icon>,
    route: "/parameters",
    component: <ListParameters />,
    protected_route: true,
    navbar: true,
    permissions: permissions.parameters,
  },
  {
    type: "collapse",
    name: "Editar Parámetros",
    key: "parameters-edit",
    icon: <Icon fontSize="small">settings</Icon>,
    route: "/parameters/:id/edit",
    component: <EditParameters />,
    protected_route: true,
    navbar: false,
    permissions: permissions.parameters[1],
  },
  {
    type: "collapse",
    name: "Proarnes",
    key: "inventory-management",
    icon: <Icon fontSize="small">inventory_2</Icon>,
    route: "/inventory", // Ruta base para el collapse, puedes ajustarla
    protected_route: true,
    navbar: true,
    collapse: [
      {
        name: "Homólogos",
        key: "homologo",
        icon: <Icon fontSize="small">people</Icon>,
        route: "/counterpart",
        component: <Counterpart />,
        protected_route: true,
        navbar: true,
        permissions: permissions.roles,
      },
      {
        name: "Rotaciones",
        key: "rotaciones",
        icon: <Icon fontSize="small">refresh</Icon>,
        route: "/rotations",
        component: <Itemrotation />,
        protected_route: true,
        navbar: true,
        permissions: permissions.roles,
      },
      {
        name: "Productos y Proveedores",
        key: "producprov",
        icon: <Icon fontSize="small">local_shipping</Icon>,
        route: "/provider",
        component: <Itemprovider />,
        protected_route: true,
        navbar: true,
        permissions: permissions.roles,
      },
      {
        name: "Arneses Producidos",
        key: "producedarnes",
        icon: <Icon fontSize="small">build</Icon>,
        route: "/producedarneces",
        component: <ArnesProduced />,
        protected_route: true,
        navbar: true,
        permissions: permissions.roles,
      },
      {
        name: "Listado de Pedidos",
        key: "ordersproduced",
        icon: <Icon fontSize="small">list_alt</Icon>,
        route: "/ordersproduced",
        component: <OrderList />,
        protected_route: true,
        navbar: true,
        permissions: permissions.roles,
      },
      {
        name: "Imágenes arnes",
        key: "imagesarnes",
        icon: <Icon fontSize="small">build</Icon>,
        route: "/imagescomponent",
        component: <ImageComponents />,
        protected_route: true,
        navbar: true,
        permissions: permissions.roles,
      },
      {
        name: "Componentes",
        key: "arnes",
        icon: <Icon fontSize="small">people</Icon>,
        route: "/proarnes",
        component: <ListArnes />,
        protected_route: true,
        navbar: true,
        permissions: permissions.roles,
      },
    ],
  },
  

];


export default routes;


// {
//   type: "collapse",
//   name: "Homologos",
//   key: "homologo",
//   icon: <Icon fontSize="small">people</Icon>,
//   route: "/counterpart",
//   component: <Counterpart />,
//   protected_route: true,
//   navbar: true,
//   permissions: permissions.roles,
// },
// {
//   type: "collapse",
//   name: "Rotaciones",
//   key: "rotaciones",
//   icon: <Icon fontSize="small">people</Icon>,
//   route: "/rotations",
//   component: <Itemrotation />,
//   protected_route: true,
//   navbar: true,
//   permissions: permissions.roles,
// },
// {
//   type: "collapse",
//   name: "Productos y proveedores",
//   key: "producprov",
//   icon: <Icon fontSize="small">people</Icon>,
//   route: "/provider",
//   component: <Itemprovider/>,
//   protected_route: true,
//   navbar: true,
//   permissions: permissions.roles,
// },
// {
//   type: "collapse",
//   name: "Arneses producidos",
//   key: "producedarnes",
//   icon: <Icon fontSize="small">people</Icon>,
//   route: "/producedarneces",
//   component: <ArnesProduced/>,
//   protected_route: true,
//   navbar: true,
//   permissions: permissions.roles,
// },
// {
//   type: "collapse",
//   name: "Listado de pedidos",
//   key: "ordersproduced",
//   icon: <Icon fontSize="small">people</Icon>,
//   route: "/ordersproduced",
//   component: <OrderList/>,
//   protected_route: true,
//   navbar: true,
//   permissions: permissions.roles,
// },